import {
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DEFAULT_SESSION_CONFIG,
  destroy,
  useWebSession,
  type ImageValidity,
} from '@Medsicheck/medsi-web-sdk';

import type {
  RppgDemographics,
  RppgFinalResult,
  RppgQuickVitals,
} from 'services/rppg/api';
import {
  getMedsiFaceValidityMessage,
  translateMedsiScanError,
} from 'lib/medsi/messages';
import {
  buildRppgUploadMetadata,
  requestGeoPosition,
} from 'lib/medsi/buildRppgUploadMetadata';
import {
  SessionState,
  type MedsiScanVitalSigns,
} from 'lib/medsi/scan-compat';
import { useRppgScanOrchestrator } from 'hooks/medsi/useRppgScanOrchestrator';
import { InfoData, InfoType } from '../../types';

const RPPG_GEO_PREFETCH_WAIT_MS = 4000;

export interface MedsiMonitorCompletePayload {
  finalResult: RppgFinalResult;
  readingId: string;
  imageValidityCounts: Record<string, number>;
}

function mapValidityToInfo(validity: ImageValidity | string): InfoData {
  if (validity === 'VALID') {
    return { type: InfoType.NONE };
  }

  return {
    type: InfoType.INSTRUCTION,
    message: getMedsiFaceValidityMessage(validity),
  };
}

function readNumericVital(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (typeof obj.bpm === 'number') return obj.bpm;
    if (typeof obj.rpm === 'number') return obj.rpm;
    if (typeof obj.percent === 'number') return obj.percent;
    if (typeof obj.value === 'number') return obj.value;
  }
  const asNumber = Number(value);
  return Number.isNaN(asNumber) ? null : asNumber;
}

function mapLiveVitals(live: RppgQuickVitals | null): MedsiScanVitalSigns {
  const hr = readNumericVital(live?.heart_rate);
  const rr = readNumericVital(live?.respiratory_rate);
  const spo2 = readNumericVital(live?.spo2);

  return {
    pulseRate: {
      value: hr != null ? Math.round(hr) : null,
      isEnabled: hr != null,
    },
    respirationRate: {
      value: rr != null ? Math.round(rr) : null,
      isEnabled: rr != null,
    },
    stress: { value: null, isEnabled: false },
    hrvSdnn: { value: null, isEnabled: false },
    spo2: {
      value: spo2 != null ? Math.round(spo2) : null,
      isEnabled: spo2 != null,
    },
    bloodPressure: { value: null, isEnabled: false },
  };
}

export function useMedsiWebMonitor(
  video: MutableRefObject<HTMLVideoElement | null>,
  startMeasuring: boolean,
  onComplete: (payload: MedsiMonitorCompletePayload) => void,
  handleValidityJSON: (validity: string) => void,
  demographics?: RppgDemographics,
) {
  const { start, attachVideo, sessionState: cameraState } = useWebSession();
  const [info, setInfo] = useState<InfoData>({ type: InfoType.NONE });
  const [error, setError] = useState<{ code: number; message?: string }>({
    code: -1,
  });
  const [sessionState, setSessionState] = useState<SessionState>(
    SessionState.IDLE,
  );
  const cameraReadyRef = useRef(false);
  const startRequestedRef = useRef(false);
  const completedReadingRef = useRef<string | null>(null);
  const lastValidityRef = useRef<string>('');

  const scan = useRppgScanOrchestrator();

  useEffect(() => {
    let cancelled = false;

    async function initCamera() {
      try {
        await start(DEFAULT_SESSION_CONFIG);
        if (cancelled || !video.current) {
          return;
        }
        await attachVideo(video.current, DEFAULT_SESSION_CONFIG);
        if (cancelled) return;
        cameraReadyRef.current = true;
        setSessionState(SessionState.ACTIVE);
        setError({ code: -1 });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        console.error('[MedsiMonitor] camera_init_failed', message);
        setError({
          code: 1,
          message: translateMedsiScanError(message, 1),
        });
        setSessionState(SessionState.TERMINATED);
      }
    }

    void initCamera();

    return () => {
      cancelled = true;
      cameraReadyRef.current = false;
      startRequestedRef.current = false;
      destroy();
    };
  }, [attachVideo, start, video]);

  useEffect(() => {
    if (cameraState !== 'ready' && sessionState !== SessionState.ACTIVE) {
      return;
    }

    let cancelled = false;

    const runPrefetch = (
      geoPosition: Awaited<ReturnType<typeof requestGeoPosition>>,
    ) => {
      if (cancelled) return;

      try {
        const metadata = buildRppgUploadMetadata(geoPosition);
        scan.prefetchUploadInit(metadata);
      } catch (err: unknown) {
        console.error('[MedsiMonitor] prefetch_upload_init_failed', err);
      }
    };

    const geoWithTimeout = Promise.race([
      requestGeoPosition(),
      new Promise<null>(resolve =>
        setTimeout(() => resolve(null), RPPG_GEO_PREFETCH_WAIT_MS),
      ),
    ]);

    void geoWithTimeout.then(runPrefetch);

    return () => {
      cancelled = true;
    };
  }, [cameraState, scan.prefetchUploadInit, sessionState]);

  useEffect(() => {
    if (!cameraReadyRef.current) return;

    if (
      startMeasuring &&
      scan.phase === 'idle' &&
      !startRequestedRef.current
    ) {
      startRequestedRef.current = true;
      setError({ code: -1 });
      void scan
        .start(demographics ?? {})
        .catch((err: unknown) => {
          startRequestedRef.current = false;
          const message = err instanceof Error ? err.message : String(err);
          console.error('[MedsiMonitor] start_scan_failed', message);
          setError({
            code: 2,
            message: translateMedsiScanError(message, 2),
          });
          setSessionState(SessionState.ACTIVE);
        });
      return;
    }

    if (
      !startMeasuring &&
      startRequestedRef.current &&
      (scan.phase === 'scanning' || scan.phase === 'initializing')
    ) {
      startRequestedRef.current = false;
      setSessionState(SessionState.ACTIVE);
      void scan.stopScan().catch((err: unknown) => {
        console.error('[MedsiMonitor] stop_scan_failed', err);
      });
    }
  }, [startMeasuring, scan.phase, scan.start, scan.stopScan, demographics]);

  useEffect(() => {
    const face = scan.faceValidity;
    if (!face) return;

    if (face.validity === lastValidityRef.current) return;
    lastValidityRef.current = face.validity;

    if (face.validity !== 'VALID') {
      handleValidityJSON(face.validity);
    }

    setInfo(
      face.isValid
        ? { type: InfoType.NONE }
        : mapValidityToInfo(face.validity),
    );
  }, [scan.faceValidity, handleValidityJSON]);

  useEffect(() => {
    if (scan.phase === 'done' && scan.finalResult && scan.readingId) {
      startRequestedRef.current = false;
      if (completedReadingRef.current === scan.readingId) return;
      completedReadingRef.current = scan.readingId;
      setSessionState(SessionState.TERMINATED);

      onComplete({
        finalResult: scan.finalResult,
        readingId: scan.readingId,
        imageValidityCounts: scan.imageValidityCounts ?? {},
      });
      return;
    }

    if (scan.phase === 'error' && scan.error) {
      startRequestedRef.current = false;
      setError({
        code: 3,
        message: translateMedsiScanError(scan.error),
      });
      setSessionState(SessionState.ACTIVE);
      return;
    }

    if (
      (scan.phase === 'scanning' || scan.phase === 'initializing') &&
      startMeasuring
    ) {
      setSessionState(SessionState.MEASURING);
      return;
    }

    if (scan.phase === 'idle' && cameraReadyRef.current) {
      startRequestedRef.current = false;
      setSessionState(SessionState.ACTIVE);
    }
  }, [
    scan.phase,
    scan.finalResult,
    scan.readingId,
    scan.error,
    scan.imageValidityCounts,
    startMeasuring,
    onComplete,
  ]);

  const vitalSigns = useMemo(
    () => mapLiveVitals(scan.liveVitals),
    [scan.liveVitals],
  );

  return {
    sessionState,
    vitalSigns,
    error,
    info,
    readingId: scan.readingId,
    phase: scan.phase,
    progress: scan.progress,
    stopScan: scan.stopScan,
  };
}

export default useMedsiWebMonitor;
